/* ns-usp-grid — <ns-usp-accordion>
 *
 * The mobile accordion for sections/ns-usp-grid.liquid. Loaded ONLY by an
 * instance whose `mobile_layout` is `rows`; a `grid` instance never requests
 * this file.
 *
 * WHY A NEW COMPONENT RATHER THAN THE THEME'S ACCORDION
 * ----------------------------------------------------
 * The theme has exactly one accordion primitive: `accordion-disclosure` in
 * assets/theme.js, wired up through snippets/accordion.liquid. It was the first
 * thing checked and it does not fit here, for three separate reasons:
 *
 *   1. It is a CUSTOMIZED BUILT-IN (`<details is="accordion-disclosure">`).
 *      Safari has never shipped customized built-ins, so on a large share of
 *      the mobile traffic this section is being restyled FOR, the element never
 *      upgrades. sections/ns-accordions-with-images-v2.liquid carries a comment
 *      saying this is what broke the V1 section, and deliberately uses plain
 *      buttons instead. Repeating the mistake here is not a trade worth making.
 *
 *   2. Its exclusivity is DOCUMENT-WIDE: `set open` closes every
 *      `details.custom-accordion-el[is="accordion-disclosure"]` in the page,
 *      not just its siblings. Opening a USP row would reach into other sections
 *      on the same template.
 *
 *   3. `<details>`/`<summary>` cannot express this section's desktop. Above
 *      769px every panel must be open and there must be no disclosure widget at
 *      all — and `open` is a DOM attribute, not something a media query can set.
 *      Forcing it would need a matchMedia listener re-syncing eight elements on
 *      every resize, and would still leave a focusable `<summary>` in a static
 *      grid. A button + a panel lets CSS own the breakpoint completely: this
 *      file never knows or cares what the viewport is.
 *
 * The other accordion in the theme (ns-accordions-with-images-v2) is a
 * per-section inline IIFE bound to its own markup, so there was nothing to
 * reuse there either. This is written as an autonomous
 * custom element per .claude/CLAUDE.md, which is the house rule the inline one
 * predates.
 *
 * WHAT THIS FILE IS AND IS NOT RESPONSIBLE FOR
 * -------------------------------------------
 * Responsible for: which row is open, keeping `aria-expanded` truthful, and
 * one-at-a-time exclusivity within THIS instance.
 *
 * Not responsible for: the initial open row (server-rendered from the block's
 * `start_open` setting, so there is no post-paint correction), hiding the
 * toggle before upgrade, or the desktop layout. All three are CSS, keyed on
 * `ns-usp-accordion:defined` and the 769px query in assets/ns-usp-grid.css.
 * That is what makes the no-JS state correct by construction rather than by
 * this file remembering to handle it.
 *
 * One delegated listener on the host, so teardown is a single removeEventListener
 * and a section re-render in the theme editor cannot leak handlers.
 */
if (!customElements.get('ns-usp-accordion')) {
  customElements.define(
    'ns-usp-accordion',
    class NsUspAccordion extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);

        /* Theme editor only: selecting a block in the sidebar should reveal it,
           otherwise a merchant editing row 6 is editing copy they cannot see. */
        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', this.handleBlockSelect);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }

      get items() {
        return Array.prototype.slice.call(this.querySelectorAll('[data-usp-item]'));
      }

      handleClick(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        const toggle = target.closest('[data-usp-toggle]');
        if (!toggle || !this.contains(toggle)) return;

        const item = toggle.closest('[data-usp-item]');
        if (!item) return;

        /* aria-expanded is the single source of truth for the current state, so
           the button cannot drift out of sync with the class that hides the
           panel. Re-tapping the open row closes it — the comp shows one row
           open, not one row permanently pinned. */
        this.setOpenItem(item, toggle.getAttribute('aria-expanded') !== 'true');
      }

      handleBlockSelect(event) {
        const item = event.target.closest('[data-usp-item]');
        if (item) this.setOpenItem(item, true);
      }

      /* Exclusivity lives here rather than in the click handler: every row is
         written on every change, so there is no path that leaves two rows open
         or an aria-expanded pointing at a hidden panel. */
      setOpenItem(target, open) {
        this.items.forEach((item) => {
          const isOpen = open && item === target;
          item.classList.toggle('is-open', isOpen);

          const toggle = item.querySelector('[data-usp-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      }
    }
  );
}
